import { format, parseISO, differenceInDays, addDays, isValid } from 'date-fns';

/**
 * Safely parses a date string or Date object with proper validation
 */
function safeParseDateString(date: string | Date): Date | null {
  if (!date) return null;
  
  try {
    if (typeof date === 'string') {
      // Handle empty or whitespace-only strings
      if (date.trim() === '') return null;
      const parsed = parseISO(date);
      return isValid(parsed) ? parsed : null;
    } else if (date instanceof Date) {
      return isValid(date) ? date : null;
    }
  } catch (error) {
    console.warn('Date parsing error:', error);
  }
  
  return null;
}

/**
 * Parses a date-only "YYYY-MM-DD" string into a LOCAL Date anchored at noon.
 *
 * This is timezone-neutral: it never uses UTC parsing (native `new Date("YYYY-MM-DD")`
 * treats the string as UTC midnight, which renders a day earlier in negative-offset
 * zones and shifts selections). Anchoring to local noon guarantees the calendar day
 * is preserved for any timezone offset. Legacy ISO timestamps (containing 'T') are
 * converted to their local calendar day first for backward compatibility.
 */
export function parseDateOnly(date: string | null | undefined): Date | null {
  if (!date) return null;
  const trimmed = date.trim();
  if (trimmed === '') return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    const localNoon = new Date(year, month - 1, day, 12, 0, 0, 0);
    // Date silently normalizes invalid values (e.g. 2027-02-31). Rejecting
    // those values keeps an invalid persisted string from becoming a different
    // business day during checkout serialization.
    return localNoon.getFullYear() === year
      && localNoon.getMonth() === month - 1
      && localNoon.getDate() === day
      ? localNoon
      : null;
  }

  // Legacy ISO timestamp: resolve to the local calendar day, then re-anchor at noon.
  const parsed = safeParseDateString(trimmed);
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12, 0, 0, 0);
}

/**
 * Gets the year from a date string or Date object for display purposes
 */
export function getYearFromDate(date: string | Date): number {
  const dateObj = safeParseDateString(date);
  if (!dateObj) {
    return new Date().getFullYear(); // Fallback to current year
  }
  return dateObj.getFullYear();
}

/**
 * Formats a date for display with safe error handling
 */
export function formatDate(date: string | Date): string {
  if (!date) return '';
  
  const dateObj = safeParseDateString(date);
  if (!dateObj) return '';
  
  try {
    return format(dateObj, 'MMM dd, yyyy');
  } catch (error) {
    console.warn('Date formatting error:', error);
    return '';
  }
}

/**
 * Formats a date for HTML input fields with safe error handling
 */
export function formatDateInput(date: string | Date): string {
  if (!date) return '';

  // Date-only values are already the booking contract. Preserve the exact
  // validated calendar date instead of parsing and reserializing through an
  // instant, which is how a selected May 31 can become May 30 in a negative
  // UTC offset.
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
    const parsedDateOnly = parseDateOnly(date);
    return parsedDateOnly ? date.trim() : "";
  }
  
  const dateObj = safeParseDateString(date);
  if (!dateObj) return '';
  
  try {
    return format(dateObj, 'yyyy-MM-dd');
  } catch (error) {
    console.warn('Date input formatting error:', error);
    return '';
  }
}

/**
 * Safely calculates the number of nights between two dates
 */
export function calculateNights(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0;
  
  const startObj = safeParseDateString(startDate);
  const endObj = safeParseDateString(endDate);
  
  if (!startObj || !endObj) return 0;
  
  try {
    const nights = differenceInDays(endObj, startObj);
    return Math.max(0, nights); // Ensure non-negative result
  } catch (error) {
    console.warn('Date calculation error:', error);
    return 0;
  }
}

/**
 * Gets tomorrow's date as a formatted string
 */
export function getTomorrowDate(): string {
  try {
    const tomorrow = addDays(new Date(), 1);
    return formatDateInput(tomorrow);
  } catch (error) {
    console.warn('Error getting tomorrow date:', error);
    // Fallback to manual calculation
    const fallback = new Date();
    fallback.setDate(fallback.getDate() + 1);
    return `${fallback.getFullYear()}-${String(fallback.getMonth() + 1).padStart(2, '0')}-${String(fallback.getDate()).padStart(2, '0')}`;
  }
}

/**
 * Gets the minimum checkout date (day after check-in) with safe validation
 */
export function getMinCheckoutDate(checkInDate: string): string {
  if (!checkInDate) return getTomorrowDate();
  
  const checkInDateObj = safeParseDateString(checkInDate);
  if (!checkInDateObj) return getTomorrowDate();
  
  try {
    const minCheckout = addDays(checkInDateObj, 1);
    return formatDateInput(minCheckout);
  } catch (error) {
    console.warn('Error calculating min checkout date:', error);
    return getTomorrowDate();
  }
}

/**
 * Safely checks if a date is in the past with proper validation
 */
export function isDateInPast(date: string): boolean {
  if (!date) return false;
  
  const dateObj = safeParseDateString(date);
  if (!dateObj) return false;
  
  try {
    const now = new Date();
    // Set time to start of day for fair comparison
    const dateOnly = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return dateOnly < todayOnly;
  } catch (error) {
    console.warn('Error checking if date is in past:', error);
    return false; // Default to false if we can't determine
  }
}

/**
 * Validates that a date range is valid (start before end, not in past)
 */
export function validateDateRange(startDate: string, endDate: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  if (!startDate) {
    errors.push('Check-in date is required');
  }
  
  if (!endDate) {
    errors.push('Check-out date is required');
  }
  
  if (!startDate || !endDate) {
    return { isValid: false, errors };
  }
  
  const startObj = safeParseDateString(startDate);
  const endObj = safeParseDateString(endDate);
  
  if (!startObj) {
    errors.push('Check-in date is invalid');
  }
  
  if (!endObj) {
    errors.push('Check-out date is invalid');
  }
  
  if (!startObj || !endObj) {
    return { isValid: false, errors };
  }
  
  if (isDateInPast(startDate)) {
    errors.push('Check-in date cannot be in the past');
  }
  
  if (startObj >= endObj) {
    errors.push('Check-out date must be after check-in date');
  }
  
  return { isValid: errors.length === 0, errors };
}

/**
 * Time-aware validation that considers property checkout times to prevent same-day conflicts
 */
export function validateDateRangeWithTimes(
  startDate: string, 
  endDate: string, 
  propertyCheckoutTime: string
): {
  isValid: boolean;
  errors: string[];
} {
  // First run basic date validation
  const basicValidation = validateDateRange(startDate, endDate);
  if (!basicValidation.isValid) {
    return basicValidation;
  }
  
  const errors: string[] = [];
  const startObj = safeParseDateString(startDate);
  const endObj = safeParseDateString(endDate);
  
  if (!startObj || !endObj) {
    return { isValid: false, errors: ['Invalid date format'] };
  }
  
  // Parse property checkout time
  const checkoutTime = parseTimeString(propertyCheckoutTime);
  if (!checkoutTime) {
    // Fallback to basic validation if checkout time is invalid
    return validateDateRange(startDate, endDate);
  }
  
  // Create full datetime objects for precise comparison
  const checkInDateTime = new Date(startObj);
  checkInDateTime.setHours(0, 0, 0, 0); // Start of day for check-in
  
  const checkOutDateTime = new Date(endObj);
  checkOutDateTime.setHours(checkoutTime.hours, checkoutTime.minutes, 0, 0); // Property checkout time
  
  // Check for same-day conflicts
  const isSameDay = checkInDateTime.toDateString() === checkOutDateTime.toDateString();
  if (isSameDay) {
    errors.push('Same-day checkout is not allowed to prevent conflicts');
  }
  
  // Calculate time difference in hours
  const timeDiffHours = (checkOutDateTime.getTime() - checkInDateTime.getTime()) / (1000 * 60 * 60);
  
  // Ensure minimum stay requirements (at least 24 hours considering checkout time)
  if (timeDiffHours < 24) {
    errors.push('Minimum stay is 1 night');
  }
  
  // Additional validation for checkout time conflicts
  const dayDiff = differenceInDays(endObj, startObj);
  if (dayDiff === 0) {
    errors.push('Check-out must be at least the day after check-in to prevent conflicts');
  }
  
  // Validate that checkout time allows proper turnaround
  if (dayDiff === 1 && checkoutTime.hours > 12) {
    // If checkout is late (after noon), ensure there's enough time for turnover
    const nextAvailableCheckin = new Date(checkOutDateTime);
    nextAvailableCheckin.setDate(nextAvailableCheckin.getDate() + 1);
    nextAvailableCheckin.setHours(0, 0, 0, 0);
    
    if (checkInDateTime < nextAvailableCheckin) {
      errors.push(`Next booking can start the day after checkout (${propertyCheckoutTime}) to allow proper cleaning`);
    }
  }
  
  return { isValid: errors.length === 0, errors };
}

/**
 * Creates a full ISO timestamp with start of day time (00:00) for check-in dates
 */
export function createCheckInTimestamp(dateStr: string): string {
  if (!dateStr) return '';
  
  const dateObj = safeParseDateString(dateStr);
  if (!dateObj) return '';
  
  try {
    const timestamp = new Date(dateObj);
    timestamp.setHours(0, 0, 0, 0); // Start of day
    return timestamp.toISOString();
  } catch (error) {
    console.warn('Error creating check-in timestamp:', error);
    return '';
  }
}

/**
 * Creates a full ISO timestamp with property checkout time for check-out dates
 */
export function createCheckOutTimestamp(dateStr: string, checkoutTime: string): string {
  if (!dateStr) return '';
  
  const dateObj = safeParseDateString(dateStr);
  if (!dateObj) return '';
  
  let parsedTime = parseTimeString(checkoutTime);
  if (!parsedTime) {
    // Fallback to 10:00 AM if checkout time is invalid
    console.warn('Invalid checkout time, using 10:00 AM as fallback');
    parsedTime = { hours: 10, minutes: 0 };
  }
  
  try {
    const timestamp = new Date(dateObj);
    timestamp.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);
    return timestamp.toISOString();
  } catch (error) {
    console.warn('Error creating check-out timestamp:', error);
    return '';
  }
}

/**
 * Gets the minimum checkout date with checkout time as full ISO timestamp
 */
export function getMinCheckoutTimestamp(checkInDate: string, checkoutTime: string): string {
  if (!checkInDate) return createCheckOutTimestamp(getTomorrowDate(), checkoutTime);
  
  const minCheckoutDate = getMinCheckoutDate(checkInDate);
  return createCheckOutTimestamp(minCheckoutDate, checkoutTime);
}

/**
 * Safely parses a time string (HH:MM format) and returns hours and minutes
 */
function parseTimeString(timeStr: string): { hours: number; minutes: number } | null {
  if (!timeStr || typeof timeStr !== 'string') return null;
  
  const timePattern = /^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i;
  const match = timeStr.trim().match(timePattern);
  
  if (!match) return null;
  
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const ampm = match[3]?.toUpperCase();
  
  if (ampm === 'PM' && hours !== 12) {
    hours += 12;
  } else if (ampm === 'AM' && hours === 12) {
    hours = 0;
  }
  
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }
  
  return { hours, minutes };
}
